import { Link as RouterLink } from 'react-router-dom'
import { frameCopy } from '@/copy'
import { cx } from '../cx'
import { Icon } from '../Icon/Icon'
import { IconButton } from '../IconButton/IconButton'
import { Menu, type MenuEntry } from '../Menu/Menu'
import styles from './Breadcrumbs.module.css'

export type Crumb = { label: string; href?: string }

// A trail is positional and static, so position plus label is a stable key
// even when two crumbs share a label.
function crumbKey(c: Crumb, i: number): string {
  return `${i}:${c.label}`
}

/**
 * Trail of crumbs: real anchors with client routing, the last one the current
 * page. Folds the middle crumbs behind a Menu of links once the trail exceeds
 * `maxVisible` (first crumb + last `maxVisible − 1`).
 */
export function Breadcrumbs({
  crumbs,
  maxVisible = 3,
  className,
}: {
  crumbs: readonly Crumb[]
  maxVisible?: number
  className?: string
}) {
  const n = Math.max(2, Math.floor(maxVisible))
  const folded = crumbs.length > n
  const tail = n - 1
  const tailStart = crumbs.length - tail

  const hiddenItems: MenuEntry[] = folded
    ? crumbs.slice(1, tailStart).map((c, j) => {
        const i = j + 1
        return c.href
          ? { id: crumbKey(c, i), label: c.label, href: c.href }
          : {
              id: crumbKey(c, i),
              label: c.label,
              disabled: true,
              onSelect: () => {},
            }
      })
    : []

  function renderCrumb(c: Crumb, i: number) {
    return (
      <li className={styles.item} key={crumbKey(c, i)}>
        {i > 0 && (
          <span className={styles.separator} aria-hidden="true">
            {frameCopy.breadcrumbs.separator}
          </span>
        )}
        {i === crumbs.length - 1 ? (
          <span className={styles.current} aria-current="page">
            {c.label}
          </span>
        ) : c.href ? (
          <RouterLink to={c.href} className={styles.crumb}>
            <span>{c.label}</span>
          </RouterLink>
        ) : (
          <span className={styles.plain}>{c.label}</span>
        )}
      </li>
    )
  }

  return (
    <nav
      aria-label={frameCopy.breadcrumbs.label}
      className={cx(styles.nav, className)}
    >
      <ol className={styles.trail}>
        {folded ? (
          <>
            {renderCrumb(crumbs[0], 0)}
            <li className={styles.item} key="overflow">
              <span className={styles.separator} aria-hidden="true">
                {frameCopy.breadcrumbs.separator}
              </span>
              <Menu
                items={hiddenItems}
                trigger={
                  <IconButton label={frameCopy.breadcrumbs.showHidden}>
                    <Icon name="dots-horizontal" />
                  </IconButton>
                }
              />
            </li>
            {crumbs
              .slice(tailStart)
              .map((c, j) => renderCrumb(c, tailStart + j))}
          </>
        ) : (
          crumbs.map(renderCrumb)
        )}
      </ol>
    </nav>
  )
}
